
import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import { CanvasContext } from "../contexts/CanvasContext";
import anime from "animejs";

import { useLocation } from 'react-router-dom';

import "../styles/canvas.css";
import {
  Button, Box, CircularProgress, IconButton
} from "@mui/material";

import PromptDock from "./PromptDock";
import ExplorationTree from "./ExplorationTree";
import LayersPanel from "./LayersPanel";
import NarrativeTimeline from "./NarrativeTimeline";
import OverlayMask from "./OverlayMask";
import AnimationScriptPanel from "./AnimationScriptPanel";
import ConceptScriptPanel from "./ConceptScriptPanel";
import PasteAnimationPanel from "./PasteAnimationPanel";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';


import {
  Code as CodeIcon,
  Lightbulb,
  ContentPaste
} from '@mui/icons-material';

export default function Canvas() {

  const [isPromptDockVisible, setIsPromptDockVisible] = useState(false);
  const [isPromptDockManuallyOpened, setIsPromptDockManuallyOpened] = useState(false);

  const toggleScriptPanel = () => {
    setShowScriptPanel(!showScriptPanel);
  };

  const toggleTextPanel = () => {
    setShowTextPanel(!showTextPanel);
  };

  const togglePasteAnimationPanel = () => {
    setShowPasteAnimationPanel(!showPasteAnimationPanel);
  }

  const handleOpenPromptDock = () => {
    setIsPromptDockVisible(true);
    setIsPromptDockManuallyOpened(true);
  };

   const {   selectedSegments, generateEdit, highlightCanvasElements, layers,
    conceptSummary, animationRef,
    setLayoutType, loading, overlayMessage,
    isPlayingAnimation, setIsPlayingAnimation,
    showScriptPanel, setShowScriptPanel,
    showTextPanel, setShowTextPanel,
    animationScript, setAnimationScript,
    queryLayoutType, setQueryLayoutType,
    setOverlayMessage, concept, setConcept, setLayers, executeAnimation
      } = useContext(CanvasContext);

  useEffect(() => {
    if (isPromptDockManuallyOpened) {
      setIsPromptDockVisible(true); // If manually opened, always show
      // alert("Prompt Dock is manually opened. It will remain visible until closed.");
    } else if (selectedSegments.length >= 2) {
      setIsPromptDockVisible(true); // If 2 or more segments, show
    } else {
      setIsPromptDockVisible(false); // Otherwise, hide
    }
  }, [selectedSegments, isPromptDockManuallyOpened]);

  // Restore highlight functionality for selected segments
  useEffect(() => {
    highlightCanvasElements(selectedSegments);
  }, [selectedSegments, layers, highlightCanvasElements]);

  const resetCanvasElementsForReplay = async () => {

    layers.forEach(layer => {
      const element = document.getElementById(layer.id);
      if (element) {
        // Reset specific animated properties
        element.style.transform = ''; // Reset transform properties
        element.style.boxShadow = ''; // Reset box-shadow properties
        element.style.opacity = ''; // Reset opacity properties (if animated)
        element.style.transition = ''; // Reset transition properties (if animated)
        element.style.animation = ''; // Reset animation properties (if animated)
        console.log(`Resetting element ${layer.id}`);
      }
    });

    setLayers([...layers]);
  }

  const runPastedAnimation = async (animation_code) => {

    resetCanvasElementsForReplay();

    setAnimationScript(animation_code);

    await executeAnimation(anime, animationScript); // Await the animation execution

    if (animationRef.current) {
      animationRef.current.play();
    }

  }

  // Getting the layout from the user choice in previous LayoutSelection page
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const canvasRef = useRef(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [layerDetails, setLayerDetails] = useState(null);
  const [showPasteAnimationPanel, setShowPasteAnimationPanel] = useState(false);

  setQueryLayoutType(queryParams.get('type'));

  useEffect(() => {
    setLayoutType(queryLayoutType);
  }, [setLayoutType]);

  return (

    <div ref={canvasRef} id="canvasContainer" style={{ position: 'relative' }}
    >

      {loading && <Box
        class="loading"
      >
        <CircularProgress />
      </Box>}

      <OverlayMask
        show={isPlayingAnimation}
        canvasRef={canvasRef}
        onClose={() => setIsPlayingAnimation(false)}
        message={overlayMessage}
      />

      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%", height: "512px" }}>

        <ExplorationTree > </ExplorationTree>

        <div
          id="canvas"
        >
          <div style={{ overflow: 'hidden !important' }}>

            {layers.map((layer, index) => (
              <img
                key={index}
                alt={layer.alt || ""}
                id={layer.id}
                style={{
                  position: 'absolute',
                  width: layer.width,
                  height: layer.height,
                  left: layer.left,
                  top: layer.top,
                  zIndex: layer.zIndex,
                }}
                src={layer.src}
              />
            ))}
          </div>
          <PromptDock
            isVisible={isPromptDockVisible}
            isManuallyOpened={isPromptDockManuallyOpened} // Pass the state
            onClose={() => {
              setIsPromptDockVisible(false);
              setIsPromptDockManuallyOpened(false);
            }}
            onGenerateEdit={generateEdit}
          />
          {!isPromptDockVisible && (
            <Button
              variant="outlined"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={handleOpenPromptDock}
              // size={isMobile ? "small" : "medium"}
              sx={{ position: 'absolute', bottom: '10px', right: '10px', zIndex: 2000, display: 'flex', alignItems: 'center' }}
            >
              Write Prompt
            </Button>
          )}

        </div>






        <LayersPanel  ></LayersPanel>
      </div>



      <NarrativeTimeline className="narrative-timeline" />

      {showScriptPanel && (
        <AnimationScriptPanel animationScript={animationScript} onClose={toggleScriptPanel} />
      )}

      {/* changed code for responsiveness */}
      <div className="iconButtonContainer" style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        display: 'flex',
        gap: '15px',
        zIndex: 2300
      }}>
        <IconButton onClick={toggleScriptPanel}>
          <CodeIcon />
        </IconButton>
        {showTextPanel && (
          <ConceptScriptPanel designConcept={concept} setDesignConcept={setConcept} onClose={toggleTextPanel} />
        )}
        <IconButton onClick={toggleTextPanel}>
          <Lightbulb />
        </IconButton>
        {showPasteAnimationPanel && (
          <PasteAnimationPanel runPastedAnimation={runPastedAnimation} animationScript={animationScript} setAnimationScript={setAnimationScript} onClose={togglePasteAnimationPanel} />
        )}
        <IconButton onClick={togglePasteAnimationPanel}>
          <ContentPaste />
        </IconButton>
      </div>



    </div>
  );
}
